import dayjs from "dayjs";
import { getDowntimeKey } from "./downtimes.ts";

export default class ResultMapper {
  list: {
    [daykey: string]: {
      elementKeys: Set<string>;
      elements: any[];
      date: dayjs.Dayjs;
    };
  } = {};

  movingList: {
    [daykey: string]: {
      elementKeys: Set<string>;
      elements: any[];
      date: dayjs.Dayjs;
    };
  } = {};

  addAutobookings(abs: any[], start: dayjs.Dayjs, end: dayjs.Dayjs) {
    for (let date = start; date <= end; date = date.add(1, "day")) {
      const daykey = date.format("YYYY-MM-DD");
      try {
        for (let ab of abs ?? []) {
          if (daykey < ab.plannedStart || daykey > ab.plannedEnd) continue;
          const yesterday = date.subtract(1, "day").format("YYYY-MM-DD");
          const elementKey = "auto" + ab.id;
          if (date.isoWeekday() > 5 && !this.list[yesterday]?.elementKeys?.has(elementKey)) continue;

          if (!(daykey in this.list)) {
            this.list[daykey] = {
              elementKeys: new Set(),
              date,
              elements: [],
            };
          }

          this.list[daykey].elementKeys.add(elementKey);
          this.list[daykey].elements.push({
            element: ab,
            hide: 0,
            type: "auto",
          });
        }
      } catch (e) {
        console.log(e, abs, start, end);
      }
    }
  }

  addNormal(date: dayjs.Dayjs, element: any, hide: boolean, zoom: number) {
    const daykey = date.format("YYYY-MM-DD");
    try {
      const baseElementKey =
        (!!element.type && element.type === "manualBooking"
          ? "m" + element.id
          : !!element.vacation
            ? "v" + element.title
            : !!element.resourceOrder
              ? "o" + element.phase.id
              : !!element.resourceBooking?.resourceOrder
                ? "b" + element.phase.id
                : "c" + element.phase.id) +
        "_" +
        hide;
      const displayKey =
        zoom < 40
          ? baseElementKey
          : baseElementKey +
            "_" +
            (!!element.orderSentAt ? "sent" : "waiting") +
            "_" +
            (element?.status ?? "") +
            "_" +
            ((element?.materialOrders || []).some((x: any) => x?.critical || x?.plantClosed === daykey) ? "CRITICAL" : "normal");
      const elementKey = displayKey + "_" + getDowntimeKey(element?.phase?.downtimes, daykey);
      if (!(daykey in this.list)) {
        this.list[daykey] = {
          elementKeys: new Set(),
          date,
          elements: [],
        };
      }
      this.list[daykey].elementKeys.add(elementKey);
      this.list[daykey].elements.push({
        element,
        hide,
        type: !!element.type && element.type === "manualBooking" ? "manual" : !!element.vacation ? "vacation" : "normal",
      });
    } catch (e) {
      console.log(e, "adding error", element, daykey);
    }
  }

  getListBack(zoom: number) {
    const dates = Object.keys(this.list).sort();
    const weekmode = zoom < 40;
    let carry: any = null;
    const output: any[] = [];

    for (let date of dates) {
      let isDifferent = !carry || carry.elementKeys.symmetricDifference(this.list[date].elementKeys).size > 0;
      let splitItUp = false;
      let fillerElements: Record<string, any[]> = {};
      if (!!carry && !isDifferent) {
        if (true || this.list[date].elements[0].type === "normal") {
          const xdiff = this.list[date].date.diff(carry.end, "days");
          splitItUp = xdiff > 1;
          if (splitItUp && weekmode && xdiff <= 3 && Object.keys(carry.elements).length < (zoom < 8 ? 300 : zoom < 20 ? 60 : 21)) {
            const xdate = this.list[date].date.day();
            const ydate = carry.end.day();
            // A missing weekend can contain a downtime boundary even when
            // Friday and Monday have the same display state.
            const canBridge = carry.elements[carry.end.format("YYYY-MM-DD")].every(({ element }: { element: any }) => {
              const downtimes = element?.phase?.downtimes;
              const key = getDowntimeKey(downtimes, carry.end.format("YYYY-MM-DD"));
              for (let gapDate = carry.end.add(1, "day"); gapDate < this.list[date].date; gapDate = gapDate.add(1, "day")) {
                if (getDowntimeKey(downtimes, gapDate.format("YYYY-MM-DD")) !== key) return false;
              }
              return true;
            });
            if (canBridge && ((xdate === 0 && ydate === 5) || (xdate === 1 && ydate === 5) || (xdate === 1 && ydate === 6))) {
              splitItUp = false;
              const dd = carry.end.add(1, "day").format("YYYY-MM-DD");
              fillerElements[dd] = [];
              for (let i = 0; i < carry.elementKeys.size; i++) {
                fillerElements[dd].push({
                  element: null,
                  hide: 0,
                  type: "filler",
                });
              }
            }
            if (!splitItUp && xdate === 1 && ydate === 5) {
              const dd = this.list[date].date.subtract(1, "day").format("YYYY-MM-DD");
              fillerElements[dd] = [];
              for (let i = 0; i < carry.elementKeys.size; i++) {
                fillerElements[dd].push({
                  element: null,
                  hide: 0,
                  type: "filler",
                });
              }
            }
          }
        } else {
          if (!weekmode) {
            splitItUp = this.list[date].date.day() === 1;
          } else {
            splitItUp = Object.keys(carry.elements).length > 20;
          }
        }
      }

      if (!carry || isDifferent || splitItUp) {
        if (carry) output.push(carry);
        carry = {
          start: this.list[date].date,
          end: this.list[date].date,
          elementKeys: this.list[date].elementKeys,
          elements: { [date]: this.list[date].elements },
        };
      } else {
        carry.end = this.list[date].date;
        carry.elements = {
          ...carry.elements,
          ...fillerElements,
          [date]: this.list[date].elements,
        };
      }
    }
    if (carry) output.push(carry);

    return output;
  }

  getMovingListBack() {
    const dates = Object.keys(this.movingList).sort();
    let carry: any = null;
    const output: any[] = [];
    for (let date of dates) {
      if (
        !carry ||
        this.movingList[date].date.diff(carry.end, "days") > 1 ||
        carry.elementKeys.difference(this.movingList[date].elementKeys).size > 0
      ) {
        if (carry) output.push(carry);
        carry = {
          start: this.movingList[date].date,
          end: this.movingList[date].date,
          elementKeys: this.movingList[date].elementKeys,
          elements: { [date]: this.movingList[date].elements },
        };
      } else {
        carry.end = this.movingList[date].date;
        carry.elements[date] = this.movingList[date].elements;
      }
    }
    if (carry) output.push(carry);
    return output;
  }

  addMoving(date: dayjs.Dayjs, element: any, changedOffset: number) {
    const daykey = date.format("YYYY-MM-DD");
    const elementKey =
      (!!element.resourceOrder ? "o" + element.resourceOrder.id : "b" + element.resourceBooking?.resourceOrder?.id) + "c" + changedOffset;
    if (!(daykey in this.movingList)) {
      this.movingList[daykey] = {
        elementKeys: new Set(),
        elements: [],
        date,
      };
    }
    this.movingList[daykey].elementKeys.add(elementKey);
    this.movingList[daykey].elements.push({ element, changedOffset });
  }
}

